import React, { useState } from 'react';

const TermsAndConditions = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [smsConsent, setSmsConsent] = useState({
    serviceConfirmations: false,
    etaUpdates: false,
    appointmentReminders: false,
    serviceFollowUps: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleSmsConsentChange = (type) => {
    setSmsConsent(prev => ({
      ...prev,
      [type]: !prev?.[type]
    }));
  };

  return (
    <div className="bg-white font-sans min-h-screen">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/assets/images/proconnect-house-icon-only-1760307561779.png" 
                alt="ProConnect Logo" 
                className="w-14 h-14 object-contain"
              />
              <div>
                <div>
                  <span className="text-xl font-bold text-blue-600">Pro</span>
                  <span className="text-xl font-bold text-gray-900">Connect</span>
                </div>
                <div className="text-xs font-medium text-gray-600">Emergency home services</div>
              </div>
            </div>
            <div className="flex items-center space-x-8">
              <a href="/" className="font-medium text-gray-600 hover:text-orange-600 transition-colors">Home</a>
              <a href="/#benefits" className="font-medium text-gray-600 hover:text-orange-600 transition-colors">Benefits</a>
              <a href="/#earnings" className="font-medium text-gray-600 hover:text-orange-600 transition-colors">Earnings</a>
              <a href="/terms-and-conditions" className="font-medium text-orange-600">Terms</a>
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Terms and Conditions
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Legal foundation for ProConnect's contractor marketplace
          </p>
          <p className="text-sm text-gray-500">
            Last updated: October 15, 2025
          </p>
        </div>

        {/* SMS Notification Consent - Prominent Placement */}
        <section className="mb-12 bg-orange-50 border border-orange-200 rounded-2xl p-8">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-orange-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">SMS Notification Consent</h2>
              <p className="text-gray-700 mb-6">
                When you call ProConnect 24/7 at <strong className="text-orange-600">844-769-4887</strong>, you consent to receive:
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {[
              { key: 'serviceConfirmations', label: 'Service confirmation texts' },
              { key: 'etaUpdates', label: 'Technician ETA updates' },
              { key: 'appointmentReminders', label: 'Appointment reminders' },
              { key: 'serviceFollowUps', label: 'Service follow-ups' }
            ]?.map((item) => (
              <div key={item?.key} className="flex items-center space-x-3 p-4 bg-white border border-gray-200 rounded-lg">
                <input
                  type="checkbox"
                  id={item?.key}
                  checked={smsConsent?.[item?.key]}
                  onChange={() => handleSmsConsentChange(item?.key)}
                  className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor={item?.key} className="text-sm font-medium text-gray-700 cursor-pointer">
                  {item?.label}
                </label>
              </div>
            ))}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Important SMS Information</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• By using our service, you agree to receive these transactional SMS messages</li>
              <li>• To opt-out, reply <strong>STOP</strong> to any message</li>
              <li>• For help, reply <strong>HELP</strong> or call 844-769-4887</li>
              <li>• Message frequency varies based on service requests</li>
              <li>• Message and data rates may apply</li>
              <li>• All messages are transactional (related to your active service request)</li>
            </ul>
          </div>
        </section>

        {/* How SMS Notifications Work */}
        <section className="mb-12 bg-blue-50 border border-blue-200 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How SMS Notifications Work</h2>
          <p className="text-gray-700 mb-6">
            Since ProConnect uses an AI voice agent that collects information during calls, here's how our SMS notification process works:
          </p>
          
          <div className="space-y-4">
            {[
              {
                step: 1,
                title: 'AI Assistant Collection',
                description: 'Our AI assistant collects your phone number when you call our 24/7 emergency hotline'
              },
              {
                step: 2,
                title: 'Verbal Consent',
                description: 'You verbally agree to receive service updates via text during the call'
              },
              {
                step: 3,
                title: 'Notification Delivery',
                description: 'We send confirmation, ETA, and completion notifications for your active service request'
              }
            ]?.map((item) => (
              <div key={item?.step} className="flex items-start space-x-4 p-4 bg-white border border-gray-200 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {item?.step}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{item?.title}</h4>
                  <p className="text-sm text-gray-600">{item?.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-100 border border-blue-300 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> All messages are transactional and related to your active service requests. 
              We do not send promotional or marketing messages via SMS.
            </p>
          </div>
        </section>

        {/* Additional Terms Sections */}
        <div className="space-y-6">
          {/* Marketplace Participation Terms */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <button 
              onClick={() => toggleSection('marketplace')}
              className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-xl font-semibold text-gray-900">Marketplace Participation Terms</h3>
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections?.marketplace ? 'rotate-180' : ''}`} 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </button>
            {expandedSections?.marketplace && (
              <div className="px-6 pb-6">
                <div className="prose prose-gray max-w-none">
                  <p>Contractors participating in the ProConnect marketplace agree to:</p>
                  <ul>
                    <li>Maintain valid Florida licensing and insurance requirements</li>
                    <li>Provide high-quality HVAC, plumbing, or electrical services</li>
                    <li>Respond to job requests within specified timeframes</li>
                    <li>Complete assigned work according to industry standards</li>
                    <li>Maintain professional conduct with customers</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Payment Terms */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <button 
              onClick={() => toggleSection('payment')}
              className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-xl font-semibold text-gray-900">Payment Processing Terms</h3>
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections?.payment ? 'rotate-180' : ''}`} 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </button>
            {expandedSections?.payment && (
              <div className="px-6 pb-6">
                <div className="prose prose-gray max-w-none">
                  <h4>Commission Structure:</h4>
                  <ul>
                    <li>Founding Contractors: 80% commission on first 7 jobs</li>
                    <li>Standard Contractors: 60% commission structure</li>
                    <li>Payments processed within 7 business days</li>
                    <li>Direct deposit to contractor's verified bank account</li>
                  </ul>
                  <h4>Payment Protection:</h4>
                  <ul>
                    <li>Customer payment card secured before service</li>
                    <li>Automatic payment processing upon job completion</li>
                    <li>Dispute resolution process for payment issues</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Licensing Requirements */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <button 
              onClick={() => toggleSection('licensing')}
              className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-xl font-semibold text-gray-900">Licensing Verification Requirements</h3>
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections?.licensing ? 'rotate-180' : ''}`} 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </button>
            {expandedSections?.licensing && (
              <div className="px-6 pb-6">
                <div className="prose prose-gray max-w-none">
                  <p>All contractors must maintain:</p>
                  <ul>
                    <li>Valid Florida contractor license for HVAC, plumbing, or electrical work</li>
                    <li>Current general liability insurance ($1M minimum)</li>
                    <li>Workers' compensation coverage (if applicable)</li>
                    <li>Bonding requirements as per Florida state regulations</li>
                    <li>Annual license renewal and verification</li>
                  </ul>
                  <p>ProConnect reserves the right to verify and re-verify licensing status at any time.</p>
                </div>
              </div>
            )}
          </div>

          {/* Dispute Resolution */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <button 
              onClick={() => toggleSection('disputes')}
              className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-xl font-semibold text-gray-900">Dispute Resolution Procedures</h3>
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections?.disputes ? 'rotate-180' : ''}`} 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </button>
            {expandedSections?.disputes && (
              <div className="px-6 pb-6">
                <div className="prose prose-gray max-w-none">
                  <h4>Resolution Process:</h4>
                  <ol>
                    <li>Direct communication between contractor and ProConnect</li>
                    <li>Mediation through ProConnect's resolution team</li>
                    <li>Third-party arbitration if necessary</li>
                    <li>Final resolution binding on all parties</li>
                  </ol>
                  <h4>Common Dispute Areas:</h4>
                  <ul>
                    <li>Payment processing delays</li>
                    <li>Job completion disagreements</li>
                    <li>Customer service issues</li>
                    <li>Platform fee disputes</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* TCPA Compliance */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <button 
              onClick={() => toggleSection('tcpa')}
              className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-xl font-semibold text-gray-900">TCPA Compliance & Communication Policies</h3>
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections?.tcpa ? 'rotate-180' : ''}`} 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </button>
            {expandedSections?.tcpa && (
              <div className="px-6 pb-6">
                <div className="prose prose-gray max-w-none">
                  <h4>SMS Communication Compliance:</h4>
                  <ul>
                    <li>All SMS messages are transactional in nature</li>
                    <li>Messages relate directly to active service requests</li>
                    <li>Contractors and customers can opt-out at any time</li>
                    <li>No promotional or marketing messages sent via SMS</li>
                    <li>Standard carrier charges may apply</li>
                  </ul>
                  <h4>Data Usage Policies:</h4>
                  <ul>
                    <li>Phone numbers used only for service-related communication</li>
                    <li>No sharing of contact information with third parties</li>
                    <li>Secure storage of all communication data</li>
                    <li>Compliance with federal and state privacy regulations</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <section className="mt-12 p-8 bg-gray-50 border border-gray-200 rounded-2xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Legal Questions & Contact</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Legal Department</h4>
              <p className="text-gray-600 mb-4">
                For questions about these terms and conditions or legal matters:
              </p>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>Email: legal@proconnect.com</li>
                <li>Phone: +1 (813) 555-0199</li>
                <li>Address: Tampa Bay, FL</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Related Documents</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors text-sm">
                    Privacy Policy →
                  </a>
                </li>
                <li>
                  <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors text-sm">
                    Contractor Agreement →
                  </a>
                </li>
                <li>
                  <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors text-sm">
                    Customer Terms of Service →
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Effective Date */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            These terms and conditions are effective as of October 15, 2025<br/>
            ProConnect reserves the right to update these terms with 30 days notice to contractors
          </p>
        </div>
      </main>
      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400">© 2025 ProConnect. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;